import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { assetUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Download, Home, Receipt, QrCode, Hash, CloudOff, RefreshCw, Loader2, Clock, Users } from "lucide-react";
import { getDb } from "@/lib/offlineStore";
import { useOnlineStatus } from "@/hooks/useConnection";
import { manualSync } from "@/lib/offlineApi";
import { onSyncChange } from "@/lib/syncEngine";
import { computeServiceTimes, formatTime } from "@/lib/serviceTime";

async function loadLocalReport(reportId) {
    const db = await getDb();
    return db.get("reports", reportId);
}

export default function TechReceipt() {
    const { id, reportId } = useParams();
    const nav = useNavigate();
    const [report, setReport] = useState(null);
    const [isLocal, setIsLocal] = useState(false);
    const [busy, setBusy] = useState(false);
    const online = useOnlineStatus();

    const load = async () => {
        // Try local first (fast). If we have a local record with _local=true, show pending UI.
        const local = await loadLocalReport(reportId);
        if (local && local._local) {
            setReport(local);
            setIsLocal(true);
            return;
        }
        // Try server
        try {
            const r = await api.get(`/reports/${reportId}`);
            setReport(r.data);
            setIsLocal(false);
        } catch (e) {
            // If we had a local fallback (post-sync, might have been replaced),
            // try to look up via the schedule's _server_report_id
            const db = await getDb();
            const sched = await db.get("schedules", id);
            if (sched?._server_report_id) {
                try {
                    const r = await api.get(`/reports/${sched._server_report_id}`);
                    setReport(r.data);
                    setIsLocal(false);
                    return;
                } catch {}
            }
            if (local) {
                setReport(local);
                setIsLocal(true);
            } else {
                toast.error("Reporte no encontrado");
            }
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [reportId, id]);

    // Auto-refresh receipt when sync engine fires (e.g. after the local report
    // is replaced by a server-generated one).
    useEffect(() => {
        const off = onSyncChange(() => { load(); });
        return off;
        // eslint-disable-next-line
    }, [reportId, id]);

    // If our local report has been replaced by a server one, navigate to the
    // canonical URL so the synced view is shown.
    useEffect(() => {
        if (!isLocal) return;
        if (!reportId?.startsWith("local-")) return;
        let cancelled = false;
        const check = async () => {
            const db = await getDb();
            const sched = await db.get("schedules", id);
            if (cancelled) return;
            if (sched?._server_report_id && sched._server_report_id !== reportId) {
                nav(`/tech/service/${id}/receipt/${sched._server_report_id}`, { replace: true });
            }
        };
        check();
        const t = setInterval(check, 3000);
        return () => { cancelled = true; clearInterval(t); };
    }, [isLocal, reportId, id, nav]);

    const handleSyncNow = async () => {
        if (!online) {
            toast.warning("Sin conexión. Reintentará automáticamente al recuperarla.");
            return;
        }
        setBusy(true);
        try {
            await manualSync();
            await load();
            toast.success("Sincronización completada");
        } finally {
            setBusy(false);
        }
    };

    if (!report) return <div className="p-6">Cargando...</div>;

    // --- Pending sync (local-only) view ---
    if (isLocal) {
        return (
            <div className="px-4 pt-4 pb-24" data-testid="tech-receipt-local">
                <div className="text-center mb-6">
                    <div className="inline-flex w-16 h-16 rounded-full bg-amber-100 items-center justify-center mb-3">
                        <CloudOff className="w-10 h-10 text-amber-600" />
                    </div>
                    <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Guardado localmente</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                        El reporte y la firma están a salvo en este dispositivo. El recibo con QR DTE y PDF se generará automáticamente cuando recuperes conexión.
                    </p>
                </div>

                <Card className="p-4 mb-4 border-amber-200 bg-amber-50/40">
                    <div className="text-xs uppercase tracking-wider text-amber-900 mb-2 flex items-center gap-1"><Receipt className="w-3.5 h-3.5" /> Resumen offline</div>
                    <div className="text-sm space-y-1.5">
                        <div className="flex justify-between"><span className="text-muted-foreground">Fecha</span><span className="font-mono">{report.service_date || "—"}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Salida</span><span className="font-mono">{formatTime(report.exit_time)}</span></div>
                        <div className="flex justify-between"><span className="text-muted-foreground">Recibido por</span><span className="font-medium">{report.accepted_by_name || "—"}</span></div>
                        <div className="flex justify-between items-center pt-2 border-t"><span className="text-muted-foreground">Productos</span><Badge variant="outline">{(report.products_used || []).length} registrados</Badge></div>
                        <div className="flex justify-between items-center"><span className="text-muted-foreground">Fotos</span><Badge variant="outline">{(report.photos || (report._local && report._photos_count) || 0)} fotos</Badge></div>
                        <div className="flex justify-between items-center"><span className="text-muted-foreground">Estado de sincronización</span><Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 border-amber-300">Pendiente</Badge></div>
                    </div>
                </Card>

                <div className="space-y-2">
                    <Button
                        className="w-full h-12 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold"
                        onClick={handleSyncNow}
                        disabled={busy || !online}
                        data-testid="receipt-sync-now"
                    >
                        {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sincronizando...</> : online ? <><RefreshCw className="w-4 h-4 mr-2" /> Sincronizar ahora</> : <><CloudOff className="w-4 h-4 mr-2" /> Esperando conexión</>}
                    </Button>
                    <Button variant="outline" className="w-full h-12" onClick={() => nav("/tech")} data-testid="receipt-back-to-route">
                        <Home className="w-4 h-4 mr-2" /> Volver a mi ruta
                    </Button>
                </div>

                <p className="text-center text-xs text-muted-foreground mt-6">
                    La factura DTE y el QR del reporte aparecerán automáticamente aquí en cuanto recuperes conexión.
                </p>
            </div>
        );
    }

    // --- Normal synced view ---
    return (
        <div className="px-4 pt-4 pb-24" data-testid="tech-receipt">
            <div className="text-center mb-6">
                <div className="inline-flex w-16 h-16 rounded-full bg-green-100 items-center justify-center mb-3">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Servicio completado</h1>
                <p className="text-sm text-muted-foreground mt-1">Reporte generado y sincronizado</p>
            </div>

            <Card className="p-4 mb-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1"><Receipt className="w-3.5 h-3.5" /> Resumen</div>
                <div className="text-sm space-y-1.5">
                    <div className="flex justify-between"><span className="text-muted-foreground">Fecha</span><span className="font-mono">{report.service_date}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Entrada</span><span className="font-mono">{formatTime(report.entry_time)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Salida</span><span className="font-mono">{formatTime(report.exit_time)}</span></div>
                    {(() => {
                        const supportCount = (report.support_technician_ids || []).length;
                        const t = computeServiceTimes(report.entry_time, report.exit_time, supportCount);
                        if (t.durationMinutes === null) return null;
                        return (
                            <>
                                <div className="flex justify-between items-center pt-2 border-t">
                                    <span className="text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Duración</span>
                                    <span className="font-mono font-semibold">{t.durationLabel}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-muted-foreground flex items-center gap-1"><Users className="w-3 h-3" /> Horas hombre ({t.totalTechnicians})</span>
                                    <span className="font-mono font-semibold">{t.manHoursLabel}</span>
                                </div>
                            </>
                        );
                    })()}
                    <div className="flex justify-between items-center pt-2 border-t"><span className="text-muted-foreground">Factura</span><Badge variant="outline" className="font-mono">{report.dte_invoice_number}</Badge></div>
                </div>
            </Card>

            <div className="grid grid-cols-2 gap-3 mb-4">
                <Card className="p-3 text-center" data-testid="receipt-qr-dte">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Factura DTE</div>
                    <Badge variant="secondary" className="text-[9px] mb-2">MOCK</Badge>
                    <div className="bg-white p-2 rounded-lg border">
                        <img src={assetUrl(report.qr_dte_url)} alt="DTE QR" className="w-full h-auto" />
                    </div>
                    <div className="font-mono text-xs mt-1">{report.dte_invoice_number}</div>
                </Card>
                <Card className="p-3 text-center" data-testid="receipt-qr-public">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Reporte web</div>
                    <Badge className="text-[9px] mb-2 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">Cliente</Badge>
                    <div className="bg-white p-2 rounded-lg border">
                        <img src={assetUrl(report.qr_report_url)} alt="Reporte QR" className="w-full h-auto" />
                    </div>
                    <div className="font-mono text-xs mt-1 flex items-center justify-center gap-1" data-testid="receipt-pin">
                        <Hash className="w-3 h-3" /> PIN: <span className="font-bold tracking-widest">{report.qr_access_pin}</span>
                    </div>
                </Card>
            </div>

            <div className="space-y-2">
                <a href={assetUrl(report.report_pdf_url)} target="_blank" rel="noreferrer" className="block" data-testid="receipt-download-pdf-button">
                    <Button className="w-full h-12 bg-[hsl(var(--trulyn-navy))] text-white hover:bg-[hsl(var(--trulyn-navy))]/90">
                        <Download className="w-4 h-4 mr-2" /> Descargar PDF
                    </Button>
                </a>
                <Button variant="outline" className="w-full h-12" onClick={() => nav("/tech")} data-testid="receipt-back-to-route">
                    <Home className="w-4 h-4 mr-2" /> Volver a mi ruta
                </Button>
            </div>

            {report.photos?.length > 0 && (
                <Card className="p-3 mt-4" data-testid="receipt-photos">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Evidencia fotográfica ({report.photos.length})</div>
                    <div className="grid grid-cols-3 gap-2">
                        {report.photos.map((ph, i) => (
                            <a key={ph.id || i} href={assetUrl(ph.file_url)} target="_blank" rel="noreferrer" className="block">
                                <div className="aspect-square rounded overflow-hidden border bg-black/5">
                                    <img src={assetUrl(ph.file_url)} alt={ph.caption || `Foto ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                                </div>
                            </a>
                        ))}
                    </div>
                </Card>
            )}

            <p className="text-center text-xs text-muted-foreground mt-6">
                <QrCode className="w-3.5 h-3.5 inline mr-1" /> El cliente puede escanear el QR del reporte e ingresar el PIN para verlo en línea.
            </p>
        </div>
    );
}
